/**
 * OnboardingModal — first-visit walkthrough for new players.
 * Auto-opens once per browser (localStorage flag). Users can dismiss and reopen
 * from the "How to Play" card on Home.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Wallet, Grid3x3, Target, Sparkles, ArrowRight, X } from 'lucide-react';

const STORAGE_KEY = 'vcg_onboarding_dismissed_v1';

const STEPS = [
  {
    icon: Wallet,
    title: 'Fund your account',
    body: (
      <>
        Tap <span className="text-orange-300 font-semibold">Login</span> in the top-right → sign up →
        deposit XRP or connect your <span className="text-white font-semibold">Xaman wallet</span>.
        Your balance shows next to your name once signed in.
      </>
    ),
    accent: 'from-orange-500/20 to-amber-500/10 border-orange-500/30',
  },
  {
    icon: Grid3x3,
    title: 'Pick a room',
    body: (
      <>
        <span className="text-emerald-300 font-semibold">XRP Pool Rooms</span> — flat entry, winner takes the pot.
        House fee drops from 9.85% → 3.21% as you play more.
        <br />
        <span className="text-purple-300 font-semibold">Prize Rooms</span> — NFTs & physical items hosted by the community.
        Flat 5.89% platform fee for buyers.
      </>
    ),
    accent: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30',
  },
  {
    icon: Target,
    title: 'Pick a slot 0–9',
    body: (
      <>
        Every room has 10 slots. Pick one. You win if your slot matches the day's
        <span className="text-amber-300 font-semibold"> Illinois Pick 3 Fireball </span>
        digit — midday & evening draws, midnight cutoff (CT). Odd digits only means
        the winning number is always in the 0–9 range.
      </>
    ),
    accent: 'from-amber-500/20 to-yellow-500/10 border-amber-500/30',
  },
  {
    icon: Sparkles,
    title: 'Win → Sell or Ship',
    body: (
      <>
        Won a physical prize? You choose:
        <br />
        <span className="text-orange-300 font-semibold">💰 Sell for cash</span> — keep 32.1% of the pool, host retains the item.
        <br />
        <span className="text-emerald-300 font-semibold">📦 Ship it</span> — free shipping, the prize is yours.
        <span className="text-slate-300">plus</span> the actual prize.
      </>
    ),
    accent: 'from-fuchsia-500/20 to-purple-500/10 border-fuchsia-500/30',
  },
];

export const OnboardingModal = ({ forceOpen, defer, onClose }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [autoOpened, setAutoOpened] = useState(false);

  useEffect(() => {
    if (forceOpen) { setOpen(true); setStep(0); setAutoOpened(false); return; }
    // When the Fireball countdown is inside its 15-min urgency window,
    // hold back the first-visit walkthrough so the Auto-Buy CTA underneath
    // stays clickable. It'll auto-open next visit if still un-dismissed.
    if (defer) return;
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) { setOpen(true); setAutoOpened(true); }
    } catch { /* localStorage blocked */ }
  }, [forceOpen, defer]);

  // If urgency kicks in after the walkthrough has auto-opened (schedule loads
  // after the modal), quietly close it (without persisting) so the CTA is free.
  useEffect(() => {
    if (defer && open && autoOpened && !forceOpen) {
      setOpen(false);
      setAutoOpened(false);
      onClose?.();
    }
  }, [defer, open, autoOpened, forceOpen, onClose]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    setOpen(false);
    onClose?.();
  };

  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : dismiss());

  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="onboarding-modal" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm p-0 gap-0 overflow-hidden">
        <div className="flex justify-between items-center px-4 pt-3 pb-2">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Step {step + 1} / {STEPS.length}
          </p>
          <button
            data-testid="onboarding-skip-btn"
            onClick={dismiss}
            className="text-slate-500 hover:text-slate-300 transition-colors"
            aria-label="Skip onboarding"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className={`px-5 py-6 border-y border-slate-700 bg-gradient-to-br ${s.accent}`}>
          <Icon className="w-8 h-8 text-white mb-3" strokeWidth={1.5} />
          <h2 className="text-xl font-black text-white leading-tight mb-2">{s.title}</h2>
          <p className="text-sm text-slate-200 leading-relaxed">{s.body}</p>
        </div>

        <div className="px-4 py-3 flex items-center gap-2">
          <div className="flex gap-1.5 flex-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i === step ? 'bg-orange-400' : i < step ? 'bg-slate-500' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
          <Button
            data-testid="onboarding-next-btn"
            onClick={next}
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold h-9 px-4"
          >
            {step < STEPS.length - 1 ? (
              <>Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></>
            ) : (
              "Let's go"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
